import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "../../SVG/ChevronDown";
import { MenuItem, MenuItemData } from "./MenuItem";
import "./ButtonWithDropdown.scss";

interface ButtonWithDropdownProps {
  disabled: boolean;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>, isOpen: boolean) => void;
  onButtonClick?: (event: React.MouseEvent<HTMLSpanElement>) => void;
  buttonType: "primary" | "secondary" | "tertiary" | "danger";
  size: "small" | "medium" | "large";
  isOpen?: boolean;
  menuData: MenuItemData[];
  alwaysOpenDropdown?: boolean;
}

export function ButtonWithDropdown(props: ButtonWithDropdownProps) {
  const { alwaysOpenDropdown = false } = props;
  const [isOpen, setIsOpen] = useState(props.isOpen ?? false);
  const [topValue, setTopValue] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonClass = `btn-dropdown btn-${props.buttonType}-${props.size}${!alwaysOpenDropdown ? " split-mode" : ""}`;

  const updateTopValue = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTopValue(rect.top);
    }
  };

  const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    updateTopValue();
    props.onClick?.(event, newIsOpen);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (alwaysOpenDropdown) {
      toggleDropdown(event);
    }
  };

  const handleContentClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!alwaysOpenDropdown) {
      event.stopPropagation();
      props.onButtonClick?.(event);
    }
  };

  const handleChevronClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!alwaysOpenDropdown) {
      event.stopPropagation();
      toggleDropdown(event);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const menuPositionClass =
    topValue > window.innerHeight * 0.65
      ? "dropdown-menu-container __bottom"
      : "dropdown-menu-container __top";

  return (
    <div className="button-with-dropdown-wrapper" ref={containerRef}>
      <button
        disabled={props.disabled}
        onClick={handleButtonClick}
        className={buttonClass}
      >
        <span 
          className="button-content" 
          onClick={handleContentClick}
        >
          {props.children}
        </span>
        <span 
          className="chevron-wrapper" 
          onClick={handleChevronClick}
        >
          <ChevronDown className={`chevron-icon ${isOpen ? "rotated" : ""}`} />
        </span>
      </button>
      
      {isOpen && (
        <div className={menuPositionClass}>
          <div className="dropdown-menu-items" role="menu">
            {props.menuData.map((item, index) => (
              <MenuItem
                key={item.id ?? index}
                label={item.label}
                disabled={item.disabled}
                checked={item.checked}
                onClick={() => {
                  if (item.disabled) return;
                  item.onClick(item);
                  const shouldClose = item.closeOnClick ?? item.checked === undefined;
                  if (shouldClose) {
                    setIsOpen(false);
                  }
                }}
                color={item.color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
